<template>
  <div class="trigger-item-list">
    <!-- TRIGGER -->
    <template v-for="(trigger, index) of triggers" :key="trigger.id">
      <div v-if="index > 0" class="trigger-item-list__separator">
        <strong>{{ $t("words.or") }}</strong>
      </div>
      <button-item
        :selected="
          variablesView.subSelection?.type == trigger.type &&
          variablesView.subSelection?.id == trigger.id
        "
        @click.prevent="select(trigger)"
        @contextmenu.prevent="openContextMenu(trigger, $event)"
      >
        <trigger-title :trigger="trigger" :index="index" />

        <template #icons>
          <button
            type="button"
            class="button-icon button-item__icon"
            @click.prevent="deleteTrigger(trigger)"
          >
            <lucide-icon name="trash-2" />
          </button>
        </template>
      </button-item>
    </template>
    <button class="button mt-2" type="button" @click.prevent="addTrigger">
      {{ $t("actions.addTrigger") }}
    </button>

    <div class="trigger-item-list__line mt-4">
      <span> {{ $t("labels.maxIncidentOccurrences") }}{{ $t(":") }} </span>
      <input-number
        :allow-null="true"
        :min="1"
        :value="incident.maxOccurrences ?? undefined"
        :icons="incident.maxOccurrences != null ? ['x'] : []"
        placeholder="∞"
        @update:value="updateIncidentKey('maxOccurrences', $event)"
        @click:x="updateIncidentKey('maxOccurrences', null)"
      />
    </div>

    <context-menu
      :visible="contextMenuState.trigger != null"
      :x="contextMenuState.x"
      :y="contextMenuState.y"
      @close="contextMenuState.trigger = null"
    >
      <button
        v-if="contextMenuState.trigger"
        type="button"
        class="context-menu__entry"
        @click="$emit('copy', contextMenuState.trigger)"
      >
        {{ $t("actions.copyTrigger") }}
      </button>

      <button
        type="button"
        class="context-menu__entry context-menu__entry--danger"
        @click="deleteTrigger(contextMenuState.trigger)"
      >
        <lucide-icon name="trash-2" class="align-middle" />
        {{ $t("actions.deleteTrigger") }}
      </button>
    </context-menu>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive } from "vue";

import InputNumber from "@core/input/InputNumber.vue";
import ButtonItem from "@core/input/ButtonItem.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";
import TriggerTitle from "./TriggerTitle.vue";
import ContextMenu from "@core/ui/ContextMenu.vue";

import { useIncidentStore } from "@editor/items/incident/incidents.store";
import { useIncidentTriggerStore } from "@editor/items/incidentTrigger/incidentTriggers.store";
import { useVariablesViewStore } from "@editor/views/variables/variablesView.store";

import type { IncidentItem } from "../incident/IncidentItem";
import type { ConditionItem } from "../condition/ConditionItem";
import type { IncidentTriggerItem } from "./IncidentTriggerItem";

const props = defineProps<{
  incidentId: number;
}>();

const emit = defineEmits<{
  (
    event: "select",
    item: IncidentItem | ConditionItem | IncidentTriggerItem
  ): void;
  (
    event: "deselect",
    item: IncidentItem | ConditionItem | IncidentTriggerItem
  ): void;
  (event: "copy", item: IncidentTriggerItem): void;
}>();

const variablesView = useVariablesViewStore();
const triggerStore = useIncidentTriggerStore();
const incidentStore = useIncidentStore();

const incident = computed(() => {
  return incidentStore.items[props.incidentId];
});
const triggers = computed(() => {
  return triggerStore.byIncident[props.incidentId];
});

async function addTrigger() {
  const trigger = await triggerStore.create({
    incidentId: props.incidentId,
    chance: 100
  });
  emit("select", trigger);
}

function deleteTrigger(trigger: IncidentTriggerItem | null) {
  if (trigger == null) return;
  triggerStore.delete(trigger);
}

function updateIncidentKey<K extends keyof IncidentItem>(
  key: K,
  value: IncidentItem[K]
) {
  incidentStore.updateKey(incident.value.id, key, value);
}

function select(item: IncidentItem | ConditionItem | IncidentTriggerItem) {
  emit("select", item);
}

const contextMenuState = reactive({
  trigger: null as IncidentTriggerItem | null,
  x: 0,
  y: 0
});

function openContextMenu(trigger: IncidentTriggerItem, event: MouseEvent) {
  contextMenuState.trigger = trigger;
  contextMenuState.x = event.clientX;
  contextMenuState.y = event.clientY;
}
</script>

<style lang="scss">
.trigger-item-list {
  &__line {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(5rem, 7rem);
    gap: 0.25rem;
    align-items: center;
  }

  &__separator {
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
    text-align: center;

    // create a thin horizontal line,
    // separating the box vertically,
    // with a gap for the word "or" in the middle
    background-image: linear-gradient(
      to right,
      var(--color-text-light) 0%,
      var(--color-text-light) calc(50% - 3rem),
      transparent calc(50% - 3rem),
      transparent calc(50% + 3rem),
      var(--color-text-light) calc(50% + 3rem),
      var(--color-text-light) 100%
    );
    background-position: center;
    background-size: 100% 1px;
    background-repeat: no-repeat;
  }
}
</style>
